/**
 * Record Fingerprint Manager
 * Handles fingerprinting and duplicate detection for import records
 */

import { supabase } from '../supabase-client.js'

/**
 * Generates a fingerprint hash from key record fields
 * Used to identify duplicate records across imports
 *
 * @param {Object} record - Sales record data
 * @param {Object} options - Fingerprinting options
 * @param {boolean} options.useEntityIds - If true, uses mapped entity IDs instead of text values (default: false)
 *
 * When useEntityIds = false (legacy/CSV mode):
 * @param {string} record.date - Sale date
 * @param {string} record.customer - Customer name
 * @param {string} record.branch - Branch name (optional)
 * @param {string} record.strain - Product strain
 * @param {string} record.product - Product type
 * @param {number} record.quantity - Quantity sold
 * @param {number} record.price - Price per unit
 *
 * When useEntityIds = true (mapped mode):
 * @param {string} record.date - Sale date
 * @param {string} record.customer_id - Customer UUID
 * @param {string} record.branch_id - Branch UUID (optional, null allowed)
 * @param {string} record.product_id - Product UUID
 * @param {number} record.quantity - Quantity sold
 * @param {number} record.price - Price per unit
 *
 * @returns {Promise<string>} SHA-256 hash fingerprint
 */
export const generateRecordFingerprint = async (record, options = {}) => {
  const { useEntityIds = false } = options

  let fingerprintString

  if (useEntityIds) {
    // ID-based fingerprinting (more reliable, uses mapped entity IDs)
    const date = record.date?.trim() || ''
    const customerId = record.customer_id?.trim() || ''
    const branchId = record.branch_id?.trim() || '' // Empty string if null/undefined
    const productId = record.product_id?.trim() || ''
    const quantity = parseFloat(record.quantity || 0).toFixed(2)
    const price = parseFloat(record.price || 0).toFixed(2)

    fingerprintString = [
      date,
      customerId,
      branchId,
      productId,
      quantity,
      price
    ].join('|||')
  } else {
    // Text-based fingerprinting (legacy mode, backward compatible)
    const date = record.date?.trim() || ''
    const customer = record.customer?.trim().toLowerCase() || ''
    const branch = record.branch?.trim().toLowerCase() || ''
    const strain = record.strain?.trim().toLowerCase() || ''
    const product = record.product?.trim().toLowerCase() || ''
    const quantity = parseFloat(record.quantity || 0).toFixed(2)
    const price = parseFloat(record.price || 0).toFixed(2)

    fingerprintString = [
      date,
      customer,
      branch,
      strain,
      product,
      quantity,
      price
    ].join('|||')
  }

  // Generate SHA-256 hash using Web Crypto API
  const encoder = new TextEncoder()
  const data = encoder.encode(fingerprintString)
  const hashBuffer = await crypto.subtle.digest('SHA-256', data)
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('')

  return hashHex
}

/**
 * Batch generates fingerprints for multiple records
 *
 * @param {Array<Object>} records - Array of sales records
 * @param {Object} options - Fingerprinting options (passed to generateRecordFingerprint)
 * @returns {Promise<Array<string>>} Array of fingerprint hashes
 */
export const generateRecordFingerprints = async (records, options = {}) => {
  const fingerprints = await Promise.all(
    records.map(record => generateRecordFingerprint(record, options))
  )
  return fingerprints
}

/**
 * Checks if a record fingerprint exists in import history
 *
 * @param {string} fingerprint - Record fingerprint hash
 * @param {string} userId - User ID (for RLS filtering)
 * @param {string} templateId - Optional template ID to scope search
 * @returns {Promise<Object|null>} Existing record history or null
 */
export const findExistingRecord = async (fingerprint, userId, templateId = null) => {
  let query = supabase
    .from('import_record_history')
    .select(`
      *,
      import_sessions!inner(user_id)
    `)
    .eq('record_fingerprint', fingerprint)
    .eq('import_sessions.user_id', userId)
    .order('created_at', { ascending: false })
    .limit(1)

  // Optionally filter by template for more precise matching
  if (templateId) {
    query = query.eq('template_id', templateId)
  }

  const { data, error } = await query

  if (error) {
    console.error('[findExistingRecord] Error:', error)
    throw error
  }

  return data && data.length > 0 ? data[0] : null
}

/**
 * Batch checks fingerprints against import history
 *
 * @param {Array<string>} fingerprints - Array of fingerprint hashes
 * @param {string} userId - User ID
 * @param {string} templateId - Optional template ID
 * @returns {Promise<Map<string, Object>>} Map of fingerprint -> history record
 */
export const findExistingRecords = async (fingerprints, userId, templateId = null) => {
  if (!fingerprints || fingerprints.length === 0) {
    return new Map()
  }

  let query = supabase
    .from('import_record_history')
    .select(`
      *,
      import_sessions!inner(user_id)
    `)
    .in('record_fingerprint', fingerprints)
    .eq('import_sessions.user_id', userId)
    .order('created_at', { ascending: false })

  if (templateId) {
    query = query.eq('template_id', templateId)
  }

  const { data, error } = await query

  if (error) {
    console.error('[findExistingRecords] Error:', error)
    throw error
  }

  // Create map of fingerprint -> most recent history record
  const historyMap = new Map()
  if (data) {
    data.forEach(record => {
      // Only keep the first (most recent) occurrence of each fingerprint
      if (!historyMap.has(record.record_fingerprint)) {
        historyMap.set(record.record_fingerprint, record)
      }
    })
  }

  return historyMap
}

/**
 * Records import history for a batch of records
 *
 * @param {Array<Object>} historyRecords - Records to save
 * @param {string} historyRecords[].sessionId - Import session ID
 * @param {string} historyRecords[].templateId - Template ID (optional)
 * @param {string} historyRecords[].fingerprint - Record fingerprint
 * @param {string} historyRecords[].action - 'imported' or 'disregarded'
 * @param {Object} historyRecords[].mappedData - Customer/branch/product IDs
 * @returns {Promise<void>}
 */
export const saveImportHistory = async (historyRecords) => {
  if (!historyRecords || historyRecords.length === 0) {
    return
  }

  const records = historyRecords.map(record => ({
    session_id: record.sessionId,
    template_id: record.templateId || null,
    record_fingerprint: record.fingerprint,
    action: record.action,
    mapped_data: record.mappedData || {}
  }))

  const { error } = await supabase
    .from('import_record_history')
    .insert(records)

  if (error) {
    console.error('[saveImportHistory] Error:', error)
    throw error
  }
}

/**
 * Gets import statistics for a template
 *
 * @param {string} templateId - Template ID
 * @returns {Promise<Object>} Stats object
 */
export const getTemplateImportStats = async (templateId) => {
  const { data: importedCount, error: importedError } = await supabase
    .from('import_record_history')
    .select('id', { count: 'exact', head: true })
    .eq('template_id', templateId)
    .eq('action', 'imported')

  const { data: disregardedCount, error: disregardedError } = await supabase
    .from('import_record_history')
    .select('id', { count: 'exact', head: true })
    .eq('template_id', templateId)
    .eq('action', 'disregarded')

  if (importedError || disregardedError) {
    throw importedError || disregardedError
  }

  return {
    importedRecords: importedCount || 0,
    disregardedRecords: disregardedCount || 0,
    totalProcessed: (importedCount || 0) + (disregardedCount || 0)
  }
}

/**
 * Deletes import history for a specific session
 * Used when abandoning or resetting an import
 *
 * @param {string} sessionId - Import session ID
 * @returns {Promise<void>}
 */
export const deleteSessionHistory = async (sessionId) => {
  const { error } = await supabase
    .from('import_record_history')
    .delete()
    .eq('session_id', sessionId)

  if (error) {
    console.error('[deleteSessionHistory] Error:', error)
    throw error
  }
}

/**
 * Gets duplicate detection summary for a set of records
 *
 * @param {Array<Object>} records - Records to check
 * @param {string} userId - User ID
 * @param {string} templateId - Template ID (optional)
 * @param {Object} options - Fingerprinting options (passed to generateRecordFingerprints)
 * @returns {Promise<Object>} Summary with counts and fingerprint map
 */
export const getDuplicateDetectionSummary = async (records, userId, templateId = null, options = {}) => {
  // Generate fingerprints for all records
  const fingerprints = await generateRecordFingerprints(records, options)

  // Check which ones exist in history
  const existingRecords = await findExistingRecords(fingerprints, userId, templateId)

  // Build summary
  let importedCount = 0
  let disregardedCount = 0
  let newRecords = 0

  const fingerprintMap = new Map()

  fingerprints.forEach((fingerprint, index) => {
    const existing = existingRecords.get(fingerprint)
    fingerprintMap.set(index, {
      fingerprint,
      existing: existing || null,
      action: existing?.action || null
    })

    if (existing) {
      if (existing.action === 'imported') {
        importedCount++
      } else if (existing.action === 'disregarded') {
        disregardedCount++
      }
    } else {
      newRecords++
    }
  })

  return {
    totalRecords: records.length,
    newRecords,
    alreadyImported: importedCount,
    alreadyDisregarded: disregardedCount,
    duplicateCount: importedCount + disregardedCount,
    fingerprintMap
  }
}
